import { connect as mqttConnect } from '@meteocontrol/mc-mqtt/dist/mqtt.min';
import { captureException } from '@sentry/browser';
import SocketEvent from './SocketEvent';
import SocketQos from './SocketQos';
import EventService from '~/zf/frontend/common/event/EventService';
import SocketConfig from './SocketConfig';

const clientEvent = {
  CLOSE: 'close',
  CONNECT: 'connect',
  DISCONNECT: 'disconnect',
  ERROR: 'error',
  MESSAGE: 'message',
};

const socketState = {
  CLIENT: null,
  CLIENT_PROMISE: null,
  CLIENT_RESOLVER: null,
};

socketState.CLIENT_PROMISE = new Promise((resolve) => {
  socketState.CLIENT_RESOLVER = resolve;
});

const subscribers = new Map();

function getClient() {
  return socketState.CLIENT_PROMISE;
}

function getTopicEventName(topic) {
  const topicEvent = topic.replace('/', '-');
  return `${SocketEvent.MESSAGE_RECEIVED}-${topicEvent}`;
}

async function bindListener() {
  const client = await getClient();
  client.on(clientEvent.CONNECT, () => {
    EventService.dispatch(SocketEvent.CONNECTED);
  });
  client.on(clientEvent.MESSAGE, (topic, payload) => {
    const messageString = payload.toString();
    const topicEvent = getTopicEventName(topic);
    const message = JSON.parse(messageString);
    EventService.dispatch(topicEvent, { data: { message } });
    EventService.dispatch(SocketEvent.MESSAGE_RECEIVED, { data: { topic, message } });
  });
  client.on(clientEvent.ERROR, () => {
    EventService.dispatch(SocketEvent.CONNECTION_ERROR);
  });
  client.on(clientEvent.CLOSE, () => {
    EventService.dispatch(SocketEvent.USER_IS_OFFLINE);
  });
  client.on(clientEvent.DISCONNECT, () => {
    EventService.dispatch(SocketEvent.USER_IS_OFFLINE);
  });
  client.on(clientEvent.OFFLINE, () => {
    EventService.dispatch(SocketEvent.USER_IS_OFFLINE);
  });
}

function addSubscriber(topic) {
  if (subscribers.has(topic) === false) {
    subscribers.set(topic, 1);
    return true;
  }
  subscribers.set(topic, subscribers.get(topic) + 1);
  return false;
}

function removeSubscriber(topic) {
  if (subscribers.has(topic) === false) {
    return true;
  }
  subscribers.set(topic, subscribers.get(topic) - 1);
  return subscribers.get(topic) === 0;
}

function getConnectionOptions(credentials) {
  return {
    username: credentials.userId.toString(),
    password: credentials.jwt,
    reconnectPeriod: SocketConfig.RECONNECT_PERIOD,
    keepalive: SocketConfig.KEEPALIVE,
    resubscribe: SocketConfig.RESUBSCRIBE,
  };
}

function connect({ address, credentials }) {
  const connectionOptions = getConnectionOptions(credentials);
  if (socketState.CLIENT === null) {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `https://${address}`, true);
    xhr.withCredentials = true;
    xhr.setRequestHeader('X-ACCESS-TOKEN', credentials.jwt);
    xhr.send(null);
    xhr.onload = () => {
      socketState.CLIENT = mqttConnect(`wss://${address}`, connectionOptions);
      socketState.CLIENT_RESOLVER(socketState.CLIENT);
      bindListener();
    };
    return;
  }

  socketState.CLIENT.options.password = connectionOptions.password;
  socketState.CLIENT.reconnect();
}

function disconnect() {
  if (socketState.CLIENT === null) {
    return;
  }
  socketState.CLIENT.end();
}

async function subscribe(topics, qos = SocketQos.AT_MOST_ONCE) {
  const client = await getClient();
  return new Promise((resolve, reject) => {
    const topicEvents = topics.reduce((accumulator, topic) => {
      const event = getTopicEventName(topic);
      return { ...accumulator, [topic]: event };
    }, {});

    const newSubscriptions = topics.filter((topic) => addSubscriber(topic));
    if (newSubscriptions.length === 0) {
      /* eslint-disable-next-line no-promise-executor-return */
      return resolve(topicEvents);
    }

    client.subscribe(topics, { qos }, (error) => {
      if (error) {
        captureException(error);
        return reject(error);
      }
      return EventService.dispatch(SocketEvent.SUBSCRIBED, { data: { topics } });
    });
    /* eslint-disable-next-line no-promise-executor-return */
    return resolve(topicEvents);
  });
}

async function unsubscribe(topic) {
  const isLast = removeSubscriber(topic);
  if (isLast === false) {
    return;
  }
  const client = await getClient();
  client.unsubscribe(topic, () => {
    EventService.dispatch(SocketEvent.UNSUBSCRIBED, { data: { topic } });
  });
}

function setDebugMode(status) {
  if (status === true) {
    localStorage.setItem('debug', 'mqttjs*');
  } else {
    localStorage.removeItem('debug');
  }
}

/**
  [Vue migration] Developer Note
  This component has a twin version for both Vue 2 and Vue 3 projects.
  When making changes, remember to update and test both versions.
 */
const SocketService = {
  getTopicEventName,
  setDebugMode,
  connect,
  disconnect,
  subscribe,
  unsubscribe,
};

export default SocketService;
